/* eslint-disable monorepo/no-internal-import */
// eslint-disable-next-line monorepo/no-internal-import
import {lazy} from '@aboutyou/lazy-imports';
import {
  ActivateAaMigrationGeneralTest,
  CouponTopBanner,
  DiscountBanner,
  DiscountsRefresh,
  FooterLayout,
  useFullScreenView,
} from '@aboutyou/logic-components';
import {AdBlockDetection} from '@aboutyou/logic-components/src/client-services/AdBlockDetection/AdBlockDetection';
import {DisableAddToHomeScreen} from '@aboutyou/logic-components/src/client-services/DisableAddToHomeScreen/DisableAddToHomeScreen';
import {RegisterDataFetcherSideEffects} from '@aboutyou/logic-components/src/features/RegisterDataFetcherSideEffects/RegisterDataFetcherSideEffects';
import {useIsCheckoutPage} from '@aboutyou/router';
import {ClientGate, ErrorBoundary, SkipLinks} from '@aboutyou/ui-components';
import React, {FC, PropsWithChildren} from 'react';
import {AppProviders} from './AppProviders';
import {Header} from './components/globals/Header/Header';
import {ConditionalUspBanner} from './components/globals/UspBanner/ConditionalUspBanner';
import {PageContentWrapper} from './PageContentWrapper';
import {RouteComponents} from './RouteComponents';
import {FooterStatic} from './components/globals/Footer/FooterStatic';
import {FooterDynamic} from './components/globals/Footer/FooterDynamic';
import {FooterDynamicADP} from './components/globals/Footer/FooterDynamicADP';

const LazyClientComponents = lazy(() =>
  import('./ClientComponents').then(mod => ({
    default: mod.ClientComponents,
  })),
);

export const App: React.FC<{}> = () => {
  return (
    <AppProviders>
      <ActivateAaMigrationGeneralTest />
      <DisableAddToHomeScreen />
      <SkipLinks />
      <DiscountsRefresh />
      <RegisterDataFetcherSideEffects />

      <ErrorBoundary renderContext="header_coupon_banner">
        <ConditionalCouponTopBanner />
      </ErrorBoundary>
      <ErrorBoundary renderContext="header_campaign_banner">
        <ConditionalDiscountBanner />
      </ErrorBoundary>

      <ConditionalHeader />

      <ErrorBoundary renderContext="usp_banner" ErrorComponent={null}>
        <ConditionalUspBanner />
      </ErrorBoundary>

      <PageContentWrapper>
        <ErrorBoundary renderContext="route_components">
          <RouteComponents />
        </ErrorBoundary>

        <ErrorBoundary renderContext="footer_dynamic">
          <DynamicFooter />
        </ErrorBoundary>
      </PageContentWrapper>

      <ErrorBoundary renderContext="footer_static">
        <StaticFooter />
      </ErrorBoundary>

      <ErrorBoundary renderContext="ad_block_detection" ErrorComponent={null}>
        <AdBlockDetection />
      </ErrorBoundary>

      <ClientGate>
        <ErrorBoundary renderContext="client_components" ErrorComponent={null}>
          <LazyClientComponents />
        </ErrorBoundary>
      </ClientGate>
    </AppProviders>
  );
};

const ConditionalHeader = () => {
  const {isHeaderDisabled} = useFullScreenView();
  if (isHeaderDisabled) {
    return null;
  }
  return (
    <ErrorBoundary renderContext="header">
      <Header />
    </ErrorBoundary>
  );
};

const ConditionalCouponTopBanner = () => {
  const isCheckoutPage = useIsCheckoutPage();
  if (isCheckoutPage) {
    return null;
  }
  return <CouponTopBanner />;
};

const ConditionalFooter: FC<PropsWithChildren> = ({children}) => {
  const {isFooterDisabled} = useFullScreenView();
  if (isFooterDisabled) {
    return null;
  }
  return children;
};

const StaticFooter = () => {
  return (
    <ConditionalFooter>
      <FooterLayout>
        <FooterStatic />
      </FooterLayout>
    </ConditionalFooter>
  );
};

const DynamicFooter = () => {
  return (
    <ConditionalFooter>
      <FooterDynamic />
      <FooterDynamicADP />
    </ConditionalFooter>
  );
};

const ConditionalDiscountBanner = () => {
  const isCheckoutPage = useIsCheckoutPage();
  if (isCheckoutPage) {
    return null;
  }
  return <DiscountBanner />;
};
